/**
 * StreamingOutputWindow
 * Narrow, scrollable output panel for agent actions (doc creation, command output).
 * Used in chat to contain raw output lines as they stream or after completion.
 */

import { useEffect, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface StreamingOutputWindowProps {
  title: string
  lines: string[]
  isStreaming: boolean
  /** Lines added (shown in green, e.g. +48) */
  linesAdded?: number
  /** Lines removed (shown in red, e.g. -3) */
  linesRemoved?: number
  /** Optional fixed height for the scroll area (default 125). */
  maxHeight?: number
  /**
   * When set, the title becomes clickable to open this document in the main viewer.
   * Pass the document path (relative to content root) or the document ID if your opener uses IDs.
   * This is an internal storage path — used only for the click handler, never rendered as text
   * (the visible `title` prop should already be a human-facing label).
   */
  documentPath?: string
  /** Full shareable webapp URL, shown in the title tooltip instead of the internal documentPath. */
  documentUrl?: string
  /**
   * Called when the user clicks the title and documentPath is set.
   * If not provided, the component dispatches a global 'open-document' event with
   * detail: { filePath: documentPath, openInNewTab: event.metaKey || event.ctrlKey }.
   * In the main app, ResizableLayout listens for that and opens the doc in the document viewer.
   */
  onOpenDocument?: (filePathOrId: string, event?: React.MouseEvent) => void
  className?: string
}

export function StreamingOutputWindow({
  title,
  lines,
  isStreaming,
  linesAdded,
  linesRemoved,
  maxHeight = 125,
  documentPath,
  documentUrl,
  onOpenDocument,
  className
}: StreamingOutputWindowProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!scrollRef.current) return
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [lines])

  const showDiffStats = linesAdded !== undefined && linesAdded > 0 || linesRemoved !== undefined && linesRemoved > 0
  const isTitleClickable = documentPath != null

  const handleTitleClick = (e: React.MouseEvent) => {
    if (!documentPath) return
    if (onOpenDocument) {
      onOpenDocument(documentPath, e)
      return
    }
    window.dispatchEvent(new CustomEvent('open-document', {
      detail: {
        filePath: documentPath,
        openInNewTab: e.metaKey || e.ctrlKey
      }
    }) as Event)
  }

  return (
    <div
      className={cn(
        'w-full rounded-lg border border-border bg-muted/30 overflow-hidden',
        className
      )}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/50 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          {isStreaming && (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground flex-shrink-0" />
          )}
          {isTitleClickable ? (
            <button
              type="button"
              onClick={handleTitleClick}
              className={cn(
                'text-sm font-medium text-foreground truncate text-left',
                'hover:text-primary hover:underline cursor-pointer',
                'focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1 rounded'
              )}
              title={documentPath ? (documentUrl ? `Open in document viewer (${documentUrl})` : 'Open in document viewer') : undefined}
            >
              {title}
            </button>
          ) : (
            <span className="text-sm font-medium text-foreground truncate">{title}</span>
          )}
        </div>
        {showDiffStats && (
          <div className="flex items-center gap-1.5 flex-shrink-0 text-xs font-medium">
            {linesAdded !== undefined && linesAdded > 0 && (
              <span className="text-green-600 dark:text-green-400">+{linesAdded}</span>
            )}
            {linesRemoved !== undefined && linesRemoved > 0 && (
              <span className="text-red-600 dark:text-red-400">-{linesRemoved}</span>
            )}
          </div>
        )}
      </div>
      <div
        ref={scrollRef}
        className="overflow-y-auto overflow-x-auto p-3 font-mono text-xs leading-relaxed whitespace-pre flex-shrink-0"
        style={{ height: `${maxHeight}px`, minHeight: `${maxHeight}px` }}
      >
        {lines.length === 0 && isStreaming && (
          <span className="text-muted-foreground">Waiting for output…</span>
        )}
        {lines.map((line, i) => (
          <div key={i} className="min-w-0">
            {line || ' '}
          </div>
        ))}
      </div>
    </div>
  )
}
